const { useState } = wp.element;

// Helpers
import { sortByKey } from '../utils/helpers';

// Components
import Retailer from './retailer';

/**
 * The Retailers component displays a list of retailers sorted by their distance from a
 * specified center location.
 *
 * @param {Object}   props                   Properties passed to the component.
 * @param {Object}   props.center            An object with `lat` and `lng` properties representing the center location.
 * @param {Object[]} props.retailers         An array of retailer data objects. Each retailer object
 *                                           should include at least latitude (`lat`) and longitude (`lng`) properties.
 * @param {Object}   props.mapInstance       A reference to the Google Map instance.
 * @param {Object}   props.mapAPI            A reference to the Google Maps API object.
 * @param {number[]} props.postcodeRetailers An array of retailer IDs that match the postcode.
 * @param {boolean}  props.locationIsSet     A boolean indicating whether the user's location is set.
 * @param {boolean}  props.showLogos         A boolean indicating whether to show logos.
 * @param {string}   props.displayMode       The display mode of the retailer (compact or large).
 * @param {number}   props.perPage           The number of retailers to display per page.
 * @param {boolean}  props.hideAddressField  If true, hide the address field
 * @param {boolean}  props.hidePhoneField    If true, hide the phone field
 * @param {boolean}  props.hideWebsiteField  If true, hide the website field
 * @return {JSX.Element|null} A JSX structure representing the retailer list, or null if map dependencies are not loaded.
 */
const Retailers = ( { center, retailers, mapInstance, mapAPI, locationIsSet, postcodeRetailers, showLogos, displayMode, perPage, hideAddressField, hidePhoneField, hideWebsiteField } ) => {
	const [ retailersLimit, setRetailersLimit ] = useState( perPage );

	if ( ! mapInstance && ! mapAPI ) {
		return null;
	}

	const retailersOffset = 0;

	const { retailerTranslations } = window.QalaB2bWholesale.translations;
	const currentCenter = new mapAPI.LatLng( center.lat, center.lng );

	const showMore = () => {
		setRetailersLimit( retailersLimit + perPage );
	};

	const retailersList = retailers.map( dealer => {
		const latLng = new mapAPI.LatLng( dealer.lat, dealer.lng );
		let distance = mapAPI.geometry.spherical.computeDistanceBetween( currentCenter, latLng );
		//convert to km

		let distanceRounded = Math.round( distance / 1000 * 10 ) / 10;

		if ( window.location.href.indexOf( '/us/' ) > -1 ) {
			const distanceMiles = distance * 0.000621371192;
			distanceRounded = Math.round( distanceMiles );
		}
		distance = distanceRounded;

		return {
			...dealer,
			distance,
		};
	} );

	const sortedRetailers = sortByKey( retailersList, 'distance' );

	const renderRetailers = renderedRetailers => {
		if ( postcodeRetailers.length > 0 && locationIsSet ) {
			renderedRetailers = renderedRetailers.filter( retailer => postcodeRetailers.includes( retailer.id ) );
		}

		return renderedRetailers.map( retailer => (
			<Retailer key={ retailer.id } data={ retailer } showLogos={ showLogos } displayMode={ displayMode } hideAddressField={ hideAddressField } hidePhoneField={ hidePhoneField } hideWebsiteField={ hideWebsiteField } />
		) ).slice( retailersOffset, retailersLimit );
	};

	return (
		<section className="retailers">
			{ displayMode === 'large' && <header className="retailers-list__header">
				<h3 className="text-muted">{ retailerTranslations.closestRetailer }</h3>
			</header> }
			<div className="retailers-list">
				{ renderRetailers( sortedRetailers ) }
			</div>
			<div className="retailers-list__footer">
				{ retailersLimit <= retailers.length && <button className="btn btn--red" onClick={ showMore }>{ retailerTranslations.showMoreRetailers }</button> }
			</div>
		</section>
	);
};

export default Retailers;
